import React, { useState } from "react";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import {
  tokens,
  fmtMoney,
  StatusPill,
  CodeChip,
  PatientStrip,
  SectionPanel,
  ClinicalEmptyState,
} from "../../shared/clinical-design";
import {
  HEALTH_STATUS_META,
  RECOMMENDED_ACTIONS,
  riskBandFor,
} from "../common/constants";

function Driver({ label, value, tone }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        px: 1.25,
        py: 0.6,
        bgcolor: tokens.surface2,
        border: `1px solid ${tokens.line}`,
        borderRadius: "4px",
      }}
    >
      <Typography sx={{ fontSize: 12.5, color: tokens.ink2 }}>{label}</Typography>
      <StatusPill tone={tone} label={String(value)} dense />
    </Stack>
  );
}

/**
 * Detalle del paciente vigilado: drivers de riesgo, gaps abiertos, costo,
 * triggers y acción recomendada ("Refer to CM" mantiene estado local hasta
 * que exista el endpoint).
 */
export default function SurveillanceDetailPanel({ row, locationLabel }) {
  const [referred, setReferred] = useState(false);

  if (!row) {
    return (
      <SectionPanel title="Patient detail">
        <ClinicalEmptyState message="Select a patient from the surveillance panel to review risk drivers." />
      </SectionPanel>
    );
  }

  const band = riskBandFor(row.riskScore);
  const hsTone = HEALTH_STATUS_META[row.healthStatus]?.tone || "muted";
  const action = RECOMMENDED_ACTIONS[row.recommendedAction] || RECOMMENDED_ACTIONS.TOUCHPOINT;
  const conditionList = (row.conditionsFull || row.conditions || "")
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);

  return (
    <Stack spacing={1.5}>
      <PatientStrip
        patient={{
          name: row.name,
          age: row.age,
          gender: row.gender,
          cipId: row.cipId,
          location: locationLabel,
          pcp: row.pcpName,
        }}
        actions={
          <StatusPill tone={band.tone} label={`${band.label} · score ${row.riskScore.toFixed(2)}`} />
        }
      />

      <SectionPanel title="Risk drivers">
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Driver label="Health status" value={row.healthStatus} tone={hsTone} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Driver
              label="Global indicator (0-7)"
              value={row.globalIndicator ?? "—"}
              tone={(row.globalIndicator ?? 0) >= 6 ? "crit" : "warn"}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Driver
              label="Open care gaps"
              value={row.openCareGaps ?? 0}
              tone={(row.openCareGaps ?? 0) >= 3 ? "crit" : (row.openCareGaps ?? 0) > 0 ? "warn" : "ok"}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Driver
              label="ER events (12m)"
              value={row.erEvents ?? 0}
              tone={(row.erEvents ?? 0) >= 2 ? "crit" : (row.erEvents ?? 0) > 0 ? "warn" : "ok"}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Driver
              label="Total cost (12m)"
              value={fmtMoney(row.totalCost)}
              tone={row.isHighCost ? "crit" : "info"}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Driver
              label="Last visit"
              value={row.lastVisit || "Unknown"}
              tone={/9|12\+/.test(row.lastVisit || "") ? "warn" : "ok"}
            />
          </Grid>
        </Grid>
      </SectionPanel>

      <SectionPanel title="Active triggers">
        {row.triggers > 0 && row.triggerDescriptions ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            {row.triggerDescriptions.split(",").map((t) => (
              <StatusPill key={t} tone="crit" label={t.trim()} dense />
            ))}
          </Stack>
        ) : (
          <Typography sx={{ fontSize: 13, color: tokens.ink3 }}>
            No active surveillance triggers for this patient.
          </Typography>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap", rowGap: 1 }}>
          <StatusPill
            tone={row.touchpointOverdue ? "crit" : "ok"}
            label={
              row.touchpointOverdue
                ? `Touchpoint overdue — ${row.daysSinceTouchpoint} days since last contact`
                : `Last touchpoint ${row.daysSinceTouchpoint} day(s) ago`
            }
            dense
          />
        </Stack>
      </SectionPanel>

      <SectionPanel title="Conditions">
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", rowGap: 0.75 }}>
          {conditionList.length > 0 ? (
            conditionList.slice(0, 14).map((c) => <CodeChip key={c} code={c} />)
          ) : (
            <Typography sx={{ fontSize: 13, color: tokens.ink3 }}>
              No condition data available.
            </Typography>
          )}
          {conditionList.length > 14 && (
            <Typography sx={{ fontSize: 12.5, color: tokens.ink3, alignSelf: "center" }}>
              +{conditionList.length - 14} more
            </Typography>
          )}
        </Stack>
      </SectionPanel>

      <SectionPanel title="Recommended action">
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
          <StatusPill tone={action.tone} label={action.label} />
          <Box sx={{ flex: 1 }} />
          {referred ? (
            <StatusPill tone="ok" label="Referred to Care Management (local preview)" />
          ) : (
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() => setReferred(true)}
              sx={{
                bgcolor: tokens.primary,
                "&:hover": { bgcolor: tokens.primary700 },
                textTransform: "none",
                fontSize: 13,
              }}
            >
              Refer to Care Management
            </Button>
          )}
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: tokens.ink3, mt: 1 }}>
          Referral is local-only in this preview — it will create a CM referral via
          the surveillance backend once available.
        </Typography>
      </SectionPanel>
    </Stack>
  );
}
